import React, { useMemo, useState } from 'react';
import { Searchbar, Surface } from 'react-native-paper';
import { Animated, Text, TouchableOpacity, View, Easing, Dimensions } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { MaterialIcons } from '@expo/vector-icons';

import { SubText } from '@luminate/luminate-native-components';

import { useAuth, useThemeContext } from '../../contexts';
import { useResponsive } from '../hooks';
import { Hero } from './heros';
import { AnimatedDrawer } from '../common';

export type BrowseTestNavigatorProps = {
  menuItems: Array<string>;
  selectedMenu: string;
  testFilter?: string;
  onFilterChange: (searchTerm: string) => void;
  onNavigationChange: (navigator: string) => void;
};

export const BrowseTestNavigator = (props: BrowseTestNavigatorProps) => {
  const { session } = useAuth();
  const { theme } = useThemeContext();
  const { screenWidth } = useResponsive();
  const [menuVisible, setMenuVisible] = useState(false);
  const [navLayout, setNavLayout] = useState({ width: Dimensions.get('window').width, height: 0 });
  const [drawerAnim] = useState(new Animated.Value(0));

  const showDrawer = () => {
    setMenuVisible(true);
    Animated.timing(drawerAnim, {
      toValue: 1,
      duration: 250,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  };

  const hideDrawer = () => {
    Animated.timing(drawerAnim, {
      toValue: 0,
      duration: 250,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start(() => {
      setMenuVisible(false);
    });
  };

  const isSelected = (menuItem: string) => menuItem === props.selectedMenu;

  const displayMenu = useMemo(() => {
    return (session?.appConfig.categoriesInMenuOnlyEnabled ?? false) || screenWidth < 956;
  }, [screenWidth, session?.appConfig?.categoriesInMenuOnlyEnabled]);

  const onNavigationChange = (value: string): void => {
    setMenuVisible(false);
    if (props.onNavigationChange) {
      props.onNavigationChange(value);
    }
  };

  const onFilterSubmitEditing = (): void => {
    if (props.onNavigationChange) {
      props.onNavigationChange(props.menuItems[props.menuItems.length - 1]);
    }
  };

  const onFilterTextChanged = (value: string): void => {
    props.onFilterChange(value);
  };

  return (
    <View testID={'browse-test-navigator'}>
      <View
        onLayout={(event) => {
          const { layout } = event.nativeEvent;
          setNavLayout({ width: layout.width, height: layout.height });
        }}
      >
        <Hero />
        <Surface
          style={{
            backgroundColor: theme.colors.primary,
            flexDirection: 'row',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          {displayMenu ? (
            <TouchableOpacity onPress={() => (menuVisible ? hideDrawer() : showDrawer())}>
              <View style={{ margin: 10, flexDirection: 'row', alignItems: 'center' }}>
                <MaterialIcons
                  testID={'browse-test-menu'}
                  name={'menu'}
                  size={24}
                  color={theme.colors.surface}
                  style={{ padding: 10 }}
                />
                <SubText
                  style={{
                    fontWeight: 'bold',
                    color: theme.colors.surface,
                    borderBottomWidth: 3,
                    borderColor: theme.colors.secondary,
                  }}
                >
                  {props.selectedMenu}
                </SubText>
              </View>
            </TouchableOpacity>
          ) : (
            <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', marginLeft: 10 }}>
              {props.menuItems.map((navigator) => {
                return (
                  <TouchableOpacity
                    key={`navigation-tab-touch-opacity-${navigator}`}
                    style={[
                      {
                        alignItems: 'center',
                        justifyContent: 'center',
                      },
                      isSelected(navigator)
                        ? {
                            borderBottomWidth: 3,
                            backgroundColor: theme.colors.primary,
                            borderColor: theme.colors.secondary,
                          }
                        : {
                            backgroundColor: theme.colors.primary,
                          },
                    ]}
                    onPress={() => onNavigationChange(navigator)}
                  >
                    <Text
                      key={`navigation-tab-text-${navigator}`}
                      style={{
                        color: theme.colors.surface,
                        fontSize: 15,
                        fontWeight: 'bold',
                        marginHorizontal: 10,
                      }}
                    >
                      {navigator}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
          <View style={{ marginVertical: 5, marginHorizontal: 10, display: 'flex' }}>
            <Searchbar
              value={props.testFilter as string}
              placeholder={'Search Tests'}
              autoCapitalize={'none'}
              autoCorrect={false}
              iconColor={theme.colors.surface}
              placeholderTextColor={theme.colors.surface}
              style={{
                backgroundColor: theme.colors.primary,
                borderWidth: 1,
                borderColor: theme.colors.surface,
              }}
              inputStyle={{
                fontSize: 15,
                color: theme.colors.surface,
                borderColor: theme.colors.surface,
              }}
              onChangeText={onFilterTextChanged}
              onSubmitEditing={onFilterSubmitEditing}
              clearIcon={() => (
                <MaterialCommunityIcons
                  color={theme.colors.surface}
                  onPress={() => onFilterTextChanged('')}
                  name={'close'}
                />
              )}
            />
            <SubText style={{ color: theme.colors.surface, fontSize: 10, paddingLeft: 30, marginTop: 5 }}>
              ex: wellness, allergies, etc...
            </SubText>
          </View>
        </Surface>
      </View>
      <AnimatedDrawer
        visible={menuVisible}
        topOffset={navLayout.height + 100}
        width={navLayout.width}
        menuItems={props.menuItems}
        selectedItem={props.selectedMenu}
        onClose={hideDrawer}
        onSelect={onNavigationChange}
      />
    </View>
  );
};
